import { defineStore } from "pinia";
import { useStorage, timestamp } from "@vueuse/core";

import { useEmployeeStore } from "./employees";

// GraphQL
import { provideApolloClient } from "@vue/apollo-composable";
import apolloClient from "../graphql/client";
import gql from "graphql-tag";
import * as Sentry from "@sentry/vue";

provideApolloClient(apolloClient);

export const useCompanyStore = defineStore("company", {
  state: () => ({
    companies: useStorage("companies", []),
    selected: useStorage("selectedCompany", {}),
    loading: false,
    lastChange: useStorage("companiesLastChange", null),
  }),
  getters: {
    allCompanies: (state) => state.companies,
    company: (state) => state.selected,
    selectedCompanyId: (state) => state.selected?.id || null,
    companyUsingCode: (state) => (companycode) => {
      return state.companies.find((c) => c.code === companycode);
    },
    companyCodeUsingId: (state) => (companyid) => {
      return state.companies.find((c) => c.id === companyid) || null;
    },
    companyUsingSlug: (state) => (slug) => {
      if (!slug) return null;
      return state.companies.find((c) => c.slug === slug) || null;
    },
  },
  actions: {
    /**
     * @param {boolean} force
     * @param {{ preferredSlug?: string | null, skipEmployeeAutoSelect?: boolean }} [options]
     */
    async getData(force = false, options = {}) {
      // Quit if already loading
      if (this.loading) return;

      if (!force) {
        // Check if data is older than 5 minutes
        // Bug: Company Selection may not stick when reloading all data. Bug report: https://trello.com/c/IaqIv1nN/235-meeting-10-12
        //if (this.lastChange && timestamp() - this.lastChange < 10000) {
          return;
        //}
      }

      this.loading = true;
      //console.log("Loading companies...");
      try {
        // use apollo client directly
        const result = await apolloClient.query({
          query: gql`
            query AppCompanies {
              companies(orderBy: { name: asc }) {
                id
                code
                name
                slug
                taxId
                rcsId
                vatId
                naceId
                personInChargeAccounting {
                  id
                  name
                  email
                  phone
                }
                personInChargeHumanResources {
                  id
                  name
                  email
                  phone
                }
                hrEnabled
              }
            }
          `,
        });
        //console.dir(result);
        if (result.data?.companies && result.data.companies.length > 0) {
          this.companies = result.data.companies;
          this.lastChange = timestamp();

          const preferredSlug = options.preferredSlug ?? null;
          const skipEmployeeAutoSelect = options.skipEmployeeAutoSelect === true;

          if (preferredSlug) {
            const match = this.companies.find((c) => c.slug === preferredSlug);
            if (match) {
              this.setCompany(match, { skipEmployeeAutoSelect });
            } else {
              this.loading = false;
              return { code: "ERROR", message: "Company not found for slug" };
            }
          } else if (this.selected?.id) {
            const match = this.companies.find((c) => c.id === this.selected.id);
            if (match) {
              this.setCompany(match, { skipEmployeeAutoSelect });
            } else {
              this.setCompany(this.companies[0], { skipEmployeeAutoSelect });
            }
          } else {
            this.setCompany(this.companies[0], { skipEmployeeAutoSelect });
          }

          this.loading = false;
          return { code: "SUCCESS", message: "Load complete" };
        }
        //console.dir(result);
        this.companies = [];
        this.loading = false;
        Sentry.captureMessage("Error while loading companies");
        return { code: "ERROR", message: "Error while loading companies" };
      } catch (e) {
        //console.dir(e);
        this.loading = false;
        Sentry.captureException(e);
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    resetData() {
      this.companies = [];
      this.selected = {};
      this.loading = false;
      this.lastChange = null;
    },
    async reloadData() {
      await this.getData(true);
    },
    /**
     * @param {object} company
     * @param {{ skipEmployeeAutoSelect?: boolean }} [options]
     */
    setCompany(company, options = {}) {
      this.selected = company;
      if (!options.skipEmployeeAutoSelect) {
        useEmployeeStore().autoSelectFirstEmployeeUsingLetters();
      }
    },
  },
});
